import React, { useState, useEffect, useRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import CartItems from './CartItems';
import Totals from './Totals';
import receiptAnalytics from './receiptAnalytics';
import Button from '../Elements/Button/Button';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import { destroySelection } from '../../actions/checkout';
import { getYear, getMonth, getDate } from '../../utils/date';
import { purifier, setCookie } from '../../utils/dom';
import { translate } from '../../utils/locale';
import { deCamel } from '../../utils/string';
import s from './Receipt.scss';

const Receipt = ({ checkout, setup, location, properties, ...props }) => {
	const [conversionFired, updateConversionState] = useState(false);
	const receiptSnippet = useRef(null);
	const [klarnaCheckoutRendered, updateKlarnaCheckoutState] = useState(false);

	useEffect(() => {
		if (typeof window !== 'undefined' && location.type !== 'RECEIPT_DEBUG' && !conversionFired && checkout.selection.items && checkout.selection.items.length > 0 && properties.siteUpdated && checkout.selection.order) {
			if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
			receiptAnalytics(checkout.selection);

			setCookie('receiptShown', 1, 365);
			updateConversionState(true);
		}

		if (checkout.selection.receiptSnippet && receiptSnippet.current && !klarnaCheckoutRendered) {
			// Insert checkout form.
			if (receiptSnippet.current.querySelector('script')) {
				const scriptContents = receiptSnippet.querySelector('script').innerHTML;
				const d = document.createElement('script');
				d.text = scriptContents;
				d.id = 'klarnaReceiptScript';
				document.body.appendChild(d);

				// Avoid re-rendering where available.
				updateKlarnaCheckoutState(true);
			}
		}

		return () => {
			if (location.type !== 'RECEIPT_DEBUG' && conversionFired) props.destroySelection();
		};
	});

	if (checkout.selectionError || !checkout.selection.items) {
		return (
			<ErrorBoundary message="Error on receipt page" debugData={{ selection: checkout.selection, error: checkout.selectionError }} />
		);
	}

	let { order = '', date = '', address = {}, shippingAddress = {} } = checkout.selection;
	const fieldOrder = ['email', 'firstName', 'lastName', 'company', 'phoneNumber', 'address1', 'address2', 'zipCode', 'city'];

	if (location.type === 'RECEIPT_DEBUG') {
		address = fieldOrder.reduce((acc, field) => ({ ...acc, [field]: deCamel(field) }), {});
		shippingAddress = fieldOrder.reduce((acc, field) => ({ ...acc, [field]: deCamel(field) }), {});
		order = 123456;
		date = `${getYear()}.${getMonth()}.${getDate()}`;
	}

	const printPage = (e) => {
		e.preventDefault();
		window.print();
	};

	return (
		<main role="main">
			<div className={s.receipt}>
				<h1 className={s.receipt_title}>
					<span className={s.receipt_title_inner}>
						<span>{ translate(setup, 'receiptThanksSignOff') }</span>
					</span>
				</h1>
				<div className={s.receipt_thanks}>
					<div className={s.receipt_thanks_inner}>
						<div>
							{ `${translate(setup, 'receiptOrderNumber')} #${order}` }<br />
							{ `${date.replace('-', '.')} CET` }
						</div>
						<div dangerouslySetInnerHTML={{ __html: purifier(translate(setup, 'receiptThanks')) }} />
					</div>
				</div>
				<ErrorBoundary message="Error rendering receipt" debugData={checkout.selection}>
					{ checkout.selection.receiptSnippet && <div ref={receiptSnippet} className={s.receipt_snippet} dangerouslySetInnerHTML={{ __html: checkout.selection.receiptSnippet }} /> }
					<div className={s.receipt_order}>
						<div className={s.receipt_order_inner}>
							<CartItems currentPage="receipt" />
							<div className={s.receipt_order_inner_totals}>
								<Totals setup={setup} selection={checkout.selection} />
							</div>
						</div>
					</div>
					<h2 className={s.receipt_title}>
						<span className={s.receipt_title_inner}>
							<span>{ translate(setup, 'receiptAddressBilling') }</span>
						</span>
					</h2>
					<div className={s.receipt_address}>
						<div className={s.receipt_address_inner}>
							{
								fieldOrder.map((field) => {
									if (!address[field]) return null;
									return (
										<span key={`address-${field}`} className={s.receipt_address_inner_row}>
											{ address[field] }
										</span>
									);
								})
							}
						</div>
					</div>
					<h2 className={s.receipt_title}>
						<span className={s.receipt_title_inner}>
							<span>{ translate(setup, 'receiptAddressShipping') }</span>
						</span>
					</h2>
					<div className={s.receipt_address}>
						<div className={s.receipt_address_inner}>
							{
								fieldOrder.map((field) => {
									if (!shippingAddress[field]) return null;
									return (
										<span key={`shippingAddress-${field}`} className={s.receipt_address_inner_row}>
											{ shippingAddress[field] }
										</span>
									);
								})
							}
						</div>
					</div>
					<div className={s.receipt_print}>
						<div className={s.receipt_print_inner}>
							<Button variantClasses={['button__inverse']} type="a" href="#print-receipt" clickEventHandler={printPage}>{ translate(setup, 'receiptPrint') }</Button>
						</div>
					</div>
				</ErrorBoundary>
			</div>
		</main>
	);
};

const mapState = ({ checkout, environment, location }) => (
	{ checkout, setup: environment.setup, properties: environment.properties, location }
);

export default hot(module)(connect(mapState, { destroySelection })(Receipt));
